import { useEffect, useState } from "react";
import type { CmmdProjectLeadSummary } from "@cmmd-forge/contracts";
import { CmmdApiError, cmmdApiClient } from "../../environments/cmmdApi";

const PROJECT_LEAD_REFRESH_MS = 30_000;

export type ProjectLeadStatusState =
  | { readonly kind: "loading" }
  | { readonly kind: "inactive"; readonly stale: boolean }
  | {
      readonly kind: "active";
      readonly summary: CmmdProjectLeadSummary;
      readonly stale: boolean;
    }
  | { readonly kind: "error"; readonly message: string };

export interface ProjectLeadStatusController {
  readonly state: ProjectLeadStatusState;
  readonly actionPending: "pause" | "resume" | null;
  readonly actionError: string | null;
  readonly refresh: () => void;
  readonly pause: () => void;
  readonly resume: () => void;
}

function isInactiveProjectLead(error: unknown): boolean {
  return error instanceof CmmdApiError && error.status === 404;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Project Lead status is unavailable.";
}

function requireSummary(summary: CmmdProjectLeadSummary | undefined): CmmdProjectLeadSummary {
  if (summary === undefined) throw new Error("Project Lead status response was empty.");
  return summary;
}

function retainAsStale(state: ProjectLeadStatusState): ProjectLeadStatusState | null {
  if (state.kind === "active") return { ...state, stale: true };
  if (state.kind === "inactive") return { ...state, stale: true };
  return null;
}

export function useProjectLeadStatus(projectId: string): ProjectLeadStatusController {
  const [state, setState] = useState<ProjectLeadStatusState>({ kind: "loading" });
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [actionPending, setActionPending] = useState<"pause" | "resume" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const summary = requireSummary(await cmmdApiClient.getProjectLeadSummary(projectId));
        if (active) setState({ kind: "active", summary, stale: false });
      } catch (error) {
        if (!active) return;
        if (isInactiveProjectLead(error)) {
          setState({ kind: "inactive", stale: false });
          return;
        }
        setState(
          (current) => retainAsStale(current) ?? { kind: "error", message: errorMessage(error) },
        );
      }
    };

    void load();
    const intervalId = window.setInterval(() => void load(), PROJECT_LEAD_REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [projectId, refreshVersion]);

  const runAction = async (action: "pause" | "resume") => {
    setActionPending(action);
    setActionError(null);
    const params = {
      idempotencyKey: `forge-project-lead-${action}-${projectId}-${crypto.randomUUID()}`,
      reason:
        action === "pause"
          ? "Paused from the CMMD project board in Forge."
          : "Resumed from the CMMD project board in Forge.",
    };
    try {
      if (action === "pause") await cmmdApiClient.pauseProjectLead(projectId, params);
      else await cmmdApiClient.resumeProjectLead(projectId, params);
      const summary = requireSummary(await cmmdApiClient.getProjectLeadSummary(projectId));
      setState({ kind: "active", summary, stale: false });
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setActionPending(null);
    }
  };

  return {
    state,
    actionPending,
    actionError,
    refresh: () => setRefreshVersion((version) => version + 1),
    pause: () => void runAction("pause"),
    resume: () => void runAction("resume"),
  };
}
